import React, { useEffect } from 'react';
import { X, BookmarkSimple, PaperPlaneTilt, SignIn, UserPlus, Sparkle } from '@phosphor-icons/react';
import { PAGE_ROUTE } from '../../lib/routes';
import { setPendingIntent, setSession, setLastLoginMethod } from '../../lib/utils';
export type AuthGateIntent = 'bookmark' | 'offer' | 'generic';
interface AuthGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  intent: AuthGateIntent;
  navigate: (page: string, context?: any) => void;
  /** Tenant slug the user was looking at — used to return them after auth. */
  tenantSlug?: string;
}
const INTENT_COPY: Record<AuthGateIntent, {
  title: string;
  body: string;
  Icon: any;
}> = {
  bookmark: {
    title: 'Salvează profilul pentru mai târziu',
    body: 'Creează-ți un cont gratuit ca să salvezi chiriași și să-i contactezi când ești gata.',
    Icon: BookmarkSimple
  },
  offer: {
    title: 'Trimite o ofertă personalizată',
    body: 'Ai nevoie de un cont de proprietar pentru a trimite oferte. Durează mai puțin de 1 minut.',
    Icon: PaperPlaneTilt
  },
  generic: {
    title: 'Conectează-te ca să continui',
    body: 'Această acțiune necesită un cont Izio.',
    Icon: SignIn
  }
};
export function AuthGateModal({
  isOpen,
  onClose,
  intent,
  navigate,
  tenantSlug
}: AuthGateModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const {
    title,
    body,
    Icon
  } = INTENT_COPY[intent];
  // Bookmark + offer intents are inherently supply-side actions —
  // skip the role-selection step in Register by pre-setting the role.
  const presetRole: 'supply' | undefined = intent === 'bookmark' || intent === 'offer' ? 'supply' : undefined;
  // Persist intent so Login/Register can return the user to the original
  // tenant anunț with the apply modal open.
  const stashIntent = () => {
    if (!tenantSlug) return;
    const type = intent === 'offer' ? 'send_offer' : 'bookmark';
    setPendingIntent({
      type,
      tenantSlug
    });
  };
  const handleLogin = () => {
    stashIntent();
    onClose();
    navigate(PAGE_ROUTE.LOGIN);
  };
  const handleRegister = () => {
    stashIntent();
    onClose();
    navigate(PAGE_ROUTE.REGISTER, presetRole ? {
      presetRole
    } : undefined);
  };
  // Prototype shortcut: instant supply test account, skipping the entire
  // auth flow. Returns the user straight to the tenant anunț with the
  // apply modal open (when the intent is "offer").
  const handleTestAccount = () => {
    setLastLoginMethod('email');
    setSession({
      name: 'Andrei Proprietar (Test)',
      email: 'test.proprietar@izio.ro',
      phone: '0722 000 000',
      avatar: 'https://i.pravatar.cc/150?img=12',
      role: 'supply',
      hasListing: true,
      subRole: 'proprietar',
      memberSince: 2026,
      bio: 'Proprietar cu 2 apartamente în Floreasca și Aviației. Caut chiriași serioși pe termen lung.'
    });
    onClose();
    if (tenantSlug && intent === 'offer') {
      navigate(PAGE_ROUTE.TENANT_ANUNT, {
        slug: tenantSlug,
        openApplyModal: true
      });
    } else if (tenantSlug) {
      navigate(PAGE_ROUTE.TENANT_ANUNT, {
        slug: tenantSlug
      });
    } else {
      navigate(PAGE_ROUTE.MARKETPLACE);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="auth-gate-title" data-id="element-2253">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-dark-1/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-2254" />

      {/* Dialog */}
      <div className="relative w-full max-w-md rounded-2xl bg-dark-2 border border-dark-4 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150" data-id="element-2255">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer z-10" aria-label="Închide" data-id="element-2256">
          <X size={18} weight="bold" data-id="element-2257" />
        </button>

        <div className="p-6 sm:p-8" data-id="element-2258">
          {/* Icon */}
          <div className="w-14 h-14 rounded-2xl bg-lime-5 border border-lime-4 flex items-center justify-center mb-5" data-id="element-2259">
            <Icon size={26} weight="duotone" className="text-lime-1" data-id="element-2260" />
          </div>

          {/* Title + body */}
          <h2 id="auth-gate-title" className="text-xl font-semibold text-light-1 mb-2" data-id="element-2261">
            {title}
          </h2>
          <p className="text-sm text-light-3 leading-relaxed mb-6" data-id="element-2262">{body}</p>

          {/* Actions */}
          <div className="space-y-2.5" data-id="element-2263">
            <button onClick={handleRegister} className="w-full h-11 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors cursor-pointer" data-id="element-2264">
              <UserPlus size={16} weight="bold" data-id="element-2265" />
              Creează cont gratuit
            </button>
            <button onClick={handleLogin} className="w-full h-11 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 font-medium text-sm inline-flex items-center justify-center gap-2 border border-dark-4 transition-colors cursor-pointer" data-id="element-2266">
              <SignIn size={16} weight="bold" data-id="element-2267" />
              Am deja cont
            </button>
          </div>

          {/* Prototype-only shortcut */}
          <div className="mt-5 pt-5 border-t border-dark-4" data-id="element-2268">
            <button onClick={handleTestAccount} className="w-full h-10 rounded-md bg-transparent hover:bg-dark-3 text-light-2 hover:text-light-1 font-medium text-xs inline-flex items-center justify-center gap-2 border border-dashed border-dark-4 transition-colors cursor-pointer" data-id="element-2269">
              <Sparkle size={14} weight="fill" className="text-lime-1" data-id="element-2270" />
              Continuă cu cont de test (proprietar)
            </button>
            <p className="text-[10px] text-light-4 text-center mt-2 uppercase tracking-wider" data-id="element-2271">
              Doar pentru prototip
            </p>
          </div>

          {/* Footnote */}
          <p className="text-xs text-light-4 text-center mt-5" data-id="element-2272">
            Continuând, ești de acord cu{' '}
            <button onClick={() => {
            onClose();
            navigate(PAGE_ROUTE.TERMENI);
          }} className="text-light-3 hover:text-light-1 underline cursor-pointer" data-id="element-2273">
              Termenii
            </button>{' '}
            și{' '}
            <button onClick={() => {
            onClose();
            navigate(PAGE_ROUTE.CONFIDENTIALITATE);
          }} className="text-light-3 hover:text-light-1 underline cursor-pointer" data-id="element-2274">
              Politica de confidențialitate
            </button>
            .
          </p>
        </div>
      </div>
    </div>;
}